"use client";

import classNames from "classnames";
import type { ReactNode } from "react";
import NextLink from "next/link";
import { Show } from "@clerk/nextjs";
import { Link } from "@cappelendamm/design-system/react";
import { formatPrice } from "@cappelendamm/e-commerce-services";
import { useSignInRedirect } from "@/auth/hooks/useSignInRedirect";
import { type PriceList } from "@/products/pricing";
import { useMemberPriceOffer } from "@/products/useMemberPriceOffer";

type MemberPriceProps = {
  originalItemPrice: number | null | undefined;
  subjectToVAT: boolean | null | undefined;
  pricelists?: PriceList[] | null | undefined;
  /** "card" in product lists, "page" on the product page */
  size?: "card" | "page";
  /** Where a guest can become a member, on sites that have book clubs */
  joinHref?: string;
  /** The usual price display, shown when there is no member price */
  children: ReactNode;
};

/**
 * Leads with the member price when the product has one, in a pill of the
 * universe's offer colour and labelled «Medlemspris». Guests see what members
 * pay; members see their own price. The ordinary price stays visible but
 * smaller, and is not struck through, because it is what everyone without a
 * membership pays.
 */
export function MemberPrice({
  originalItemPrice,
  subjectToVAT,
  pricelists,
  size = "card",
  joinHref,
  children,
}: MemberPriceProps) {
  const offer = useMemberPriceOffer({
    originalItemPrice,
    subjectToVAT,
    pricelists,
  });

  if (!offer) return children;

  const page = size === "page";

  return (
    <div
      className={classNames(
        "flex flex-col items-start font-normal",
        page ? "gap-2" : "gap-1",
      )}
    >
      <p
        className={classNames(
          "flex flex-wrap items-center gap-y-1",
          page ? "gap-x-3" : "gap-x-1.5 sm:gap-x-2",
        )}
      >
        {/* The price in a pill of the universe's offer colour */}
        <span
          className={classNames(
            "rounded-full bg-(--cvr-tag-background--promoted) leading-snug font-semibold whitespace-nowrap text-(--cvr-tag-contrast--promoted) tabular-nums",
            page
              ? "px-4 py-0.5 text-[2rem]"
              : "px-2 py-px text-[1.375rem] sm:px-2.5",
          )}
        >
          {formatPrice(offer.memberPrice)}
        </span>
        <span
          className={classNames(
            "font-semibold whitespace-nowrap",
            page ? "text-base" : "text-[0.8125rem] sm:text-sm",
          )}
        >
          {/* Product lists use the same label for everyone */}
          {page && offer.isMember ? "Din medlemspris" : "Medlemspris"}
        </span>
      </p>
      <p
        className={classNames(
          "text-gray-600",
          page ? "text-base" : "text-[0.8125rem] sm:text-sm",
        )}
      >
        {offer.comparisonIsCampaign ? "Tilbudspris" : "Ordinær pris"}{" "}
        {formatPrice(offer.comparisonPrice)}
      </p>
      {page && !offer.isMember ? <MemberPriceNote joinHref={joinHref} /> : null}
    </div>
  );
}

/** Under the price on the product page: who the price is for, and how to get it */
function MemberPriceNote({ joinHref }: { joinHref?: string }) {
  const signIn = useSignInRedirect({ returnHere: true });

  const signInButton = (
    <Link
      as="button"
      type="button"
      onClick={signIn}
      variant="inline"
      className="cursor-pointer font-semibold whitespace-nowrap"
    >
      Logg inn
    </Link>
  );
  const joinLink = (label: string) =>
    joinHref ? (
      <Link
        as={NextLink}
        href={joinHref}
        variant="inline"
        className="font-semibold whitespace-nowrap"
      >
        {label}
      </Link>
    ) : null;

  // Without a club chooser this is not a club's own site, so name the clubs
  return (
    <p className="text-sm">
      Gjelder kun for {joinHref ? "medlemmer" : "bokklubbmedlemmer"}.{" "}
      <Show when="signed-out">
        {joinHref ? (
          <>
            {signInButton} eller {joinLink("bli medlem")}.
          </>
        ) : (
          <>Er du medlem? {signInButton}.</>
        )}
      </Show>
      <Show when="signed-in">{joinLink("Bli medlem")}</Show>
    </p>
  );
}
